import { ref, computed, watch } from 'vue'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import {
  prospectsLeadsService,
  prospectPreferencesService
} from '../../services/prospectService.js'
import { employeeService, teamService } from '../../services/employeeService.js'
import { useAvailability } from '../data/useAvailability.js'
import { workflowService } from '../../services/workflowService.js'
import { invalidateNavBadges } from '../ui/useNavBadges'
import { getStandardStatusLabel, getWorkflowStatusBadgeClass } from '@/utils/statusBadge'

/**
 * Composable for managing project detail data, workflow, and preferences
 */
export function useProjectDetail(prospectId, user) {
  const queryClient = useQueryClient()
  const { getWeeklyAvailability } = useAvailability()

  // Fetch prospect data
  const {
    data: prospect,
    isLoading,
    error
  } = useQuery({
    queryKey: ['prospect', prospectId],
    queryFn: async () => {
      const prospects = await prospectsLeadsService.getAll()
      return prospects.find(p => p.id === parseInt(prospectId.value)) || null
    },
    enabled: computed(() => !!prospectId.value),
    staleTime: 1000 * 60 * 5 // 5 minutes
  })

  // Employee preferences
  const preferences = ref([])
  const isSaving = ref(false)
  const isSubmitting = ref(false)
  const submissionType = ref(null) // 'submit' or 'resubmit' - tracks which action is in progress
  const saveMessage = ref('')
  const invalidDpwIndexes = ref(new Set())
  let saveMessageTimeoutId = null // Track timeout to prevent race conditions

  // Workflow status
  const workflowStatus = ref(null)
  const isLoadingWorkflow = ref(false)
  const isInitialLoadComplete = ref(false)
  const isPreferencesLoaded = ref(false)
  const isEmployeesLoaded = ref(false)

  // Data from database
  const teams = ref([])
  const employees = ref([])
  const weeklyAvailability = ref({}) // Map of employee_id -> weekly data

  // Teams to exclude from the responsible team dropdown
  const excludedTeamNames = ['enable', 'externen']

  // Load teams
  const loadTeams = async () => {
    try {
      const allTeams = await teamService.getAll()
      teams.value = allTeams.filter(
        t => !t.name?.startsWith('_OUD_') && !excludedTeamNames.includes(t.name?.toLowerCase())
      )
    } catch (err) {
      console.error('Error loading teams:', err)
    }
  }

  // Load workflow status and preferences when prospect is available
  const loadWorkflowAndPreferences = async () => {
    if (!prospectId.value) return

    isLoadingWorkflow.value = true
    isPreferencesLoaded.value = false
    try {
      // Load workflow status
      const workflow = await workflowService.getByProspect(prospectId.value)
      workflowStatus.value = workflow

      // Load existing preferences
      const existingPrefs = await prospectPreferencesService.getByProspect(prospectId.value)
      if (existingPrefs && existingPrefs.length > 0) {
        // Map database format to frontend format
        preferences.value = existingPrefs.map(pref => ({
          id: pref.id,
          role: pref.role || '',
          teams: Array.isArray(pref.teams) ? pref.teams : JSON.parse(pref.teams || '[]'),
          experienceLevels: Array.isArray(pref.experience_levels)
            ? pref.experience_levels
            : JSON.parse(pref.experience_levels || '[]'),
          dpw: parseFloat(pref.dpw) || 0,
          preferredEmployees: Array.isArray(pref.preferred_employees)
            ? pref.preferred_employees
            : JSON.parse(pref.preferred_employees || '[]'),
          notes: pref.notes || '',
          startDate: pref.start_date ? pref.start_date.split('T')[0] : null,
          endDate: pref.end_date ? pref.end_date.split('T')[0] : null
        }))
      } else {
        // Reset preferences when project has none
        preferences.value = []
      }
    } catch (err) {
      console.error('Error loading workflow/preferences:', err)
    } finally {
      isLoadingWorkflow.value = false
      isPreferencesLoaded.value = true
      // Check if both loads are complete
      if (isEmployeesLoaded.value) {
        isInitialLoadComplete.value = true
      }
    }
  }

  // Track the current date range for weekly availability
  const currentAvailabilityRange = ref({ start: null, end: null })

  // Calculate the required date range based on project and all preference dates
  const getRequiredDateRange = () => {
    // Start with project dates
    const rawProjectStart = prospect.value?.start_date || new Date().toISOString().split('T')[0]
    const rawProjectEnd =
      prospect.value?.expected_closing_date ||
      new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]

    let minDate = rawProjectStart.split('T')[0]
    let maxDate = rawProjectEnd.split('T')[0]

    // Check all preferences for custom dates that extend beyond project range
    for (const pref of preferences.value) {
      if (pref.startDate && pref.startDate < minDate) {
        minDate = pref.startDate
      }
      if (pref.endDate && pref.endDate > maxDate) {
        maxDate = pref.endDate
      }
    }

    return { start: minDate, end: maxDate }
  }

  // Load employees with weekly availability
  const loadEmployeesWithAvailability = async (forceReload = false) => {
    if (!prospect.value) return

    const requiredRange = getRequiredDateRange()

    // Skip if we already have data for this range (unless forced)
    if (
      !forceReload &&
      currentAvailabilityRange.value.start === requiredRange.start &&
      currentAvailabilityRange.value.end === requiredRange.end
    ) {
      return
    }

    // Load sourceable employees with basic info (without availability calculation to avoid 500 errors)
    try {
      const employeesData = await employeeService.getAll({ is_sourceable: 'true' })
      employees.value = employeesData
    } catch (err) {
      console.error('Error loading employees:', err)
    }

    // Load weekly availability for the required date range (via cached composable)
    try {
      const data = await getWeeklyAvailability(requiredRange.start, requiredRange.end)

      // Create a map of employee_id -> weekly availability data
      const availMap = {}
      for (const emp of data) {
        availMap[emp.simplicate_id] = emp.weeks || []
      }
      weeklyAvailability.value = availMap
      currentAvailabilityRange.value = requiredRange
    } catch (err) {
      console.error('Error loading weekly availability:', err)
    }
  }

  // Reload availability with a specific mode (used when dropdown changes)
  const reloadAvailabilityWithMode = async mode => {
    if (!prospect.value) return

    const requiredRange = getRequiredDateRange()

    try {
      const data = await getWeeklyAvailability(requiredRange.start, requiredRange.end, true, mode)

      const availMap = {}
      for (const emp of data) {
        availMap[emp.simplicate_id] = emp.weeks || []
      }
      weeklyAvailability.value = availMap
    } catch (err) {
      console.error('Error reloading weekly availability with mode:', err)
    }
  }

  // Reload weekly availability when preference dates change
  const checkAndReloadAvailability = () => {
    const requiredRange = getRequiredDateRange()
    const current = currentAvailabilityRange.value

    // If no data loaded yet, or preferences require a wider date range, reload
    if (
      !current.start ||
      !current.end ||
      requiredRange.start < current.start ||
      requiredRange.end > current.end
    ) {
      loadEmployeesWithAvailability(true)
    }
  }

  // Watch prospect ID to load workflow and preferences
  watch(
    prospectId,
    newId => {
      if (newId) {
        isInitialLoadComplete.value = false
        isPreferencesLoaded.value = false
        isEmployeesLoaded.value = false
        loadWorkflowAndPreferences()
      }
    },
    { immediate: true }
  )

  // Watch for prospect changes to load employees with correct availability period
  watch(
    prospect,
    async newProspect => {
      if (newProspect) {
        await loadEmployeesWithAvailability()
        isEmployeesLoaded.value = true
        // Mark initial load as complete only when both loads are done
        if (isPreferencesLoaded.value) {
          isInitialLoadComplete.value = true
        }
      }
    },
    { immediate: true }
  )

  // Watch for preference date changes to reload availability if needed
  watch(
    () => preferences.value.map(p => `${p.startDate}-${p.endDate}`).join(','),
    () => {
      if (isInitialLoadComplete.value) {
        checkAndReloadAvailability()
      }
    }
  )

  // Add a new preference
  const addPreference = () => {
    preferences.value.push({
      role: '',
      teams: [],
      experienceLevels: [],
      dpw: 0,
      preferredEmployees: [],
      notes: '',
      startDate: null,
      endDate: null
    })
  }

  // Remove a preference
  const removePreference = index => {
    preferences.value.splice(index, 1)
  }

  // Validate preferences
  const validatePreferences = () => {
    invalidDpwIndexes.value.clear()

    if (preferences.value.length === 0) {
      saveMessage.value = 'Voeg minimaal één voorkeur toe'
      return false
    }

    for (let i = 0; i < preferences.value.length; i++) {
      const pref = preferences.value[i]
      if (!pref.dpw || pref.dpw <= 0) {
        invalidDpwIndexes.value.add(i)
      }
    }

    if (invalidDpwIndexes.value.size > 0) {
      // Don't show message in footer - inline validation on cards shows the error
      return false
    }

    return true
  }

  // Save preferences to database
  // skipReload: when true, don't reload after save (used during submit to prevent flickering)
  const savePreferences = async (skipReload = false) => {
    if (!validatePreferences()) return false

    isSaving.value = true
    saveMessage.value = ''
    // Clear any pending message timeout to prevent it from clearing new messages
    if (saveMessageTimeoutId) {
      clearTimeout(saveMessageTimeoutId)
      saveMessageTimeoutId = null
    }

    try {
      const userInfo = {
        name: user.value?.name || 'Onbekend',
        email: user.value?.email || 'onbekend@frisseblikken.com'
      }

      const result = await prospectPreferencesService.bulkUpdate(
        prospectId.value,
        preferences.value,
        userInfo
      )

      if (result.success) {
        if (!skipReload) {
          // Reload to get IDs from database
          await loadWorkflowAndPreferences()
        }
        return true
      } else {
        saveMessage.value = result.error || 'Fout bij opslaan'
        return false
      }
    } catch (err) {
      console.error('Error saving preferences:', err)
      saveMessage.value = err.response?.data?.error || 'Fout bij opslaan van voorkeuren'
      return false
    } finally {
      isSaving.value = false
      // Clear message after 3 seconds (only if not skipping reload)
      if (!skipReload) {
        saveMessageTimeoutId = setTimeout(() => {
          saveMessage.value = ''
          saveMessageTimeoutId = null
        }, 3000)
      }
    }
  }

  // Submit for review
  const submitForReview = async ({ responsible_team_id } = {}) => {
    if (preferences.value.length === 0) {
      saveMessage.value = 'Voeg minimaal één medewerker voorkeur toe voordat je indient'
      return false
    }

    if (!validatePreferences()) return false

    // Set submitting state early to prevent button flickering
    isSubmitting.value = true
    submissionType.value = 'submit'

    // First save the preferences
    const saved = await savePreferences(true) // Skip reload during submit
    if (!saved) {
      isSubmitting.value = false
      submissionType.value = null
      return false
    }
    try {
      const userInfo = {
        name: user.value?.name || 'Onbekend',
        email: user.value?.email || 'onbekend@frisseblikken.com'
      }

      const result = await workflowService.submit(prospectId.value, userInfo, {
        responsible_team_id
      })

      if (result.success) {
        workflowStatus.value = result.data
        // Invalidate beoordelen badge - new submission waiting for review
        invalidateNavBadges(queryClient, { beoordelen: true })
        return true
      } else {
        saveMessage.value = result.error || 'Fout bij indienen'
        return false
      }
    } catch (err) {
      console.error('Error submitting for review:', err)
      saveMessage.value = err.response?.data?.error || 'Fout bij indienen'
      return false
    } finally {
      isSubmitting.value = false
      submissionType.value = null
    }
  }

  // Resubmit after rejection
  const resubmitForReview = async ({ responsible_team_id } = {}) => {
    if (preferences.value.length === 0) {
      saveMessage.value = 'Voeg minimaal één medewerker voorkeur toe voordat je opnieuw indient'
      return false
    }

    if (!validatePreferences()) return false

    // Set submitting state early to prevent button flickering
    isSubmitting.value = true
    submissionType.value = 'resubmit'

    // First save the updated preferences
    const saved = await savePreferences(true) // Skip reload during resubmit
    if (!saved) {
      isSubmitting.value = false
      submissionType.value = null
      return false
    }
    try {
      const userInfo = {
        name: user.value?.name || 'Onbekend',
        email: user.value?.email || 'onbekend@frisseblikken.com'
      }

      const result = await workflowService.resubmit(prospectId.value, userInfo, null, {
        responsible_team_id
      })

      if (result.success) {
        workflowStatus.value = result.data
        // Invalidate beoordelen badge - resubmission waiting for review
        invalidateNavBadges(queryClient, { beoordelen: true })
        return true
      } else {
        saveMessage.value = result.error || 'Fout bij opnieuw indienen'
        return false
      }
    } catch (err) {
      console.error('Error resubmitting:', err)
      saveMessage.value = err.response?.data?.error || 'Fout bij opnieuw indienen'
      return false
    } finally {
      isSubmitting.value = false
      submissionType.value = null
    }
  }

  // Workflow status helpers
  const workflowStatusLabel = computed(
    () => getStandardStatusLabel(workflowStatus.value?.status) || 'Onbekend'
  )

  const workflowStatusColor = computed(() =>
    getWorkflowStatusBadgeClass(workflowStatus.value?.status)
  )

  const canEdit = computed(() => {
    // Wait for initial load to complete to avoid flickering buttons
    if (!isInitialLoadComplete.value) return false
    // Keep buttons stable during submission (they show loading state via disabled)
    if (isSubmitting.value) return true
    // Can edit when status is 'nieuw', 'ingediend', or 'afgekeurd'
    // This allows submitters to adjust their preferences after submission
    return (
      !workflowStatus.value ||
      ['nieuw', 'ingediend', 'afgekeurd', 'heraanvraag'].includes(workflowStatus.value.status)
    )
  })

  const canEditTeam = computed(() => {
    if (!isInitialLoadComplete.value) return false
    if (isSubmitting.value) return true
    // Team mag aangepast worden zolang de kans op Aanvragen staat
    return (
      !workflowStatus.value ||
      ['nieuw', 'ingediend', 'afgekeurd', 'heraanvraag'].includes(workflowStatus.value.status)
    )
  })

  const canSubmit = computed(() => {
    // Wait for initial load to complete to avoid flickering buttons
    if (!isInitialLoadComplete.value) return false
    // Keep submit button visible during submission (shows loading state)
    if (submissionType.value === 'submit') return true
    return workflowStatus.value?.status === 'nieuw' && preferences.value.length > 0
  })

  const canResubmit = computed(() => {
    // Wait for initial load to complete to avoid flickering buttons
    if (!isInitialLoadComplete.value) return false
    // Keep resubmit button visible during resubmission (shows loading state)
    if (submissionType.value === 'resubmit') return true
    return ['afgekeurd', 'heraanvraag'].includes(workflowStatus.value?.status)
  })

  return {
    // Data
    prospect,
    isLoading,
    error,
    preferences,
    teams,
    employees,
    weeklyAvailability,
    workflowStatus,
    isLoadingWorkflow,

    // State
    isSaving,
    isSubmitting,
    saveMessage,
    invalidDpwIndexes,
    isInitialLoadComplete,

    // Computed
    workflowStatusLabel,
    workflowStatusColor,
    canEdit,
    canEditTeam,
    canSubmit,
    canResubmit,

    // Methods
    loadTeams,
    loadWorkflowAndPreferences,
    loadEmployeesWithAvailability,
    reloadAvailabilityWithMode,
    addPreference,
    removePreference,
    validatePreferences,
    savePreferences,
    submitForReview,
    resubmitForReview
  }
}
